// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkToolbarColor, greyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { IconRendererProps } from "components/work_path/IconRenderer";
import { dgIconNames } from "hooks/dgIcons";
import { ChunkAxis } from "hooks/documentDgml";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Engine } from "sdv/engine/Engine"
import { AppState } from "store";
import { setSelectionMode } from "store/documents/actions";
import { CanvasSelectionMode } from "store/documents/types";
import styled from "styled-components";
import { CanvasSelectionModeEventSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useNavigationTools } from "./NavigationTools";
import { ToolbarItem, ToolbarItemProps, ToolType } from "./ToolbarItem";
import { AutomationId } from '../../utils/Automation'
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { setExpandRepeatsEnabled } from "store/sdvSlice";

const RootContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    border-radius: 5px;
    background: ${primaryTheme.palette.white};
    align-items: center;
    padding: 4px 10px;
    box-sizing: border-box;
`

const Divider = styled.div`
    height: 35px;
    border-left: 2px solid ${greyText}80;
`

export interface ToolbarButton {
    type: ToolType;
    iconProps: IconRendererProps;
    axisId?: ChunkAxis;
}

interface ChunkToolbarProps {
    engine: Engine | null;
    documentId: string;
    canLink: boolean;
    onLink: () => void;
    hidden?: boolean;
    excludedTools?: ToolType[];
    isLinking?: boolean;
    editText: () => void;
    textEditDisabled: boolean;
    sdvCanvas?: HTMLElement;
}

export const ChunkToolbar = (props: ChunkToolbarProps): JSX.Element => {
    const isExpandRepeatsEnabled = useAppSelector(state => !!state.sdv.document?.isExpandRepeatsEnabled);
    const { tools: navigationTools } = useNavigationTools(props.documentId, props.engine);

    const { editText, textEditDisabled } = props;

    const dispatch = useAppDispatch();
    const selectionMode = useSelector((state: AppState) => state.documents.selectionMode);
    const selectionModeLocked = useSelector((state: AppState) => !!state.documents.selectionModeLocked);

    const linkButton: ToolbarItemProps = useMemo(() => {
        return {
            id: ToolType.Link,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarLinkIcon,
                    height: 1,
                    width: 1,
                    defaultColor: chunkToolbarColor
                },
                width: 20,
                height: 20
            },
            isSelected: !!props.isLinking,
            onClick: props.onLink,
            disabled: !props.canLink
        }
    }, [props.canLink, props.isLinking, props.onLink])

    const selectionButton: ToolbarItemProps = useMemo(() => {
        function goToSelectMode() {
            Telemetry.trackEvent<CanvasSelectionModeEventSchema>({
                mode: CanvasSelectionMode[CanvasSelectionMode.Select],
                eventName: TelemetryEvent.CanvasSelectionModeEvent
            });
            dispatch(setSelectionMode(CanvasSelectionMode.Select));
        }

        return {
            id: ToolType.SelectionMode,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarPointer,
                    height: 1,
                    width: 0.62,
                    defaultColor: chunkToolbarColor
                },
                width: 12.4,
                height: 20
            },
            isSelected: selectionMode === CanvasSelectionMode.Select && !props.isLinking,
            onClick: goToSelectMode,
            disabled: selectionModeLocked
        }
    }, [dispatch, selectionMode, props.isLinking, selectionModeLocked])

    const highlightButton: ToolbarItemProps = useMemo(() => {
        function goToHighlightMode() {
            Telemetry.trackEvent<CanvasSelectionModeEventSchema>({
                mode: CanvasSelectionMode[CanvasSelectionMode.Highlight],
                eventName: TelemetryEvent.CanvasSelectionModeEvent
            });
            dispatch(setSelectionMode(CanvasSelectionMode.Highlight));
        }

        return {
            id: ToolType.HighlighterMode,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarHighlighter,
                    height: 1,
                    width: 1,
                    defaultColor: chunkToolbarColor
                },
                width: 20,
                height: 20
            },
            isSelected: selectionMode === CanvasSelectionMode.Highlight && !props.isLinking,
            onClick: goToHighlightMode,
            disabled: selectionModeLocked
        }
    }, [dispatch, selectionMode, props.isLinking, selectionModeLocked])

    const editButton: ToolbarItemProps = useMemo(() => {
        function goToEditMode() {
            Telemetry.trackNamedEvent(TelemetryEvent.CanvasSelectionModeEvent, {
                mode: CanvasSelectionMode[CanvasSelectionMode.TextEdit]
            });
            editText();
        }

        return {
            id: ToolType.TextEdit,
            iconProps: {
                source: {
                    name: dgIconNames.TextEdit,
                    height: 1,
                    width: 1.4,
                    defaultColor: chunkToolbarColor
                },
                width: 25,
                height: 18
            },
            isSelected: selectionMode === CanvasSelectionMode.TextEdit && !props.isLinking,
            onClick: goToEditMode,
            disabled: textEditDisabled
        }
    }, [selectionMode, props.isLinking, editText, textEditDisabled])

    const repeatsButton: ToolbarItemProps = {
        id: ToolType.RepeatsEditor,
        iconProps: {
            source: {
                name: 'MultiSelect',
                height: 15,
                width: 13,
                defaultColor: chunkToolbarColor
            },
            width: 20,
            height: 20
        },
        isSelected: false,
        onClick: () => dispatch(setExpandRepeatsEnabled(!isExpandRepeatsEnabled))
    }

    const tools = [[selectionButton], [highlightButton], [editButton], ...navigationTools, [linkButton], [repeatsButton]];

    return (
        <RootContainer data-automation-id={AutomationId.SDVChunkToolbar} key='chunk_toolbar' hidden={props.hidden} >
            {
                tools.flatMap((x: ToolbarItemProps[], index) => {
                    const filteredGroup = x.filter(y => !props.excludedTools?.includes(y.id));
                    if (filteredGroup.length === 0) {
                        return null;
                    }

                    const elements: JSX.Element[] = [];
                    if (index !== 0) {
                        elements.push(
                            <Divider key={`divider_${index}`} />
                        );
                    }

                    elements.push(...filteredGroup.map((y: ToolbarItemProps) => <ToolbarItem key={`${y.iconProps.source.name}_${y.id}`} {...y} />));
                    return elements;
                })
            }
        </RootContainer>
    );
}
